import { CLIENT_ID } from '../config';
import { API_BASE, request } from './httpClient';
import { tokenStore } from './tokenStore';

export interface RegisterPayload {
  first_name: string;
  surname: string;
  email: string;
  password: string;
  confirm_password: string;
  username: string;
  turnstile_token?: string;
  country_code?: string;
  creation_method?: string;
}

const PKCE_VERIFIER_KEY = 'gate.haus.pkce_verifier';
const OAUTH_STATE_KEY = 'gate.haus.oauth_state';

const savePkceVerifier = (verifier: string) => sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
const getPkceVerifier = () => sessionStorage.getItem(PKCE_VERIFIER_KEY);
const clearPkceVerifier = () => sessionStorage.removeItem(PKCE_VERIFIER_KEY);

const saveOAuthState = (state: string) => sessionStorage.setItem(OAUTH_STATE_KEY, state);
const getOAuthState = () => sessionStorage.getItem(OAUTH_STATE_KEY);
const clearOAuthState = () => sessionStorage.removeItem(OAUTH_STATE_KEY);

const base64UrlEncode = (input: ArrayBuffer | Uint8Array) => {
  const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const createRandomString = (length = 32) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const createCodeChallenge = async (verifier: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

export const authService = {
  register: (payload: RegisterPayload) =>
    request<{ success: boolean; data: { message: string } }>(
      '/v2/auth/register.php',
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    ),
  buildAuthorizationUrl: async (params: { redirectUri: string; scope: string; state?: string }) => {
    const verifier = createRandomString(64);
    const challenge = await createCodeChallenge(verifier);
    const state = params.state ?? createRandomString(24);

    savePkceVerifier(verifier);
    saveOAuthState(state);

    const query = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: params.redirectUri,
      response_type: 'code',
      scope: params.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256'
    });

    return `${API_BASE}/v2/auth/authorise.php?${query.toString()}`;
  },
  exchangeCode: async (payload: { code: string; redirectUri: string; state?: string }) => {
    const storedState = getOAuthState();
    if (storedState && payload.state && storedState !== payload.state) {
      clearPkceVerifier();
      clearOAuthState();
      throw new Error('Invalid OAuth state. Please try again.');
    }

    const verifier = getPkceVerifier();
    if (!verifier) {
      clearOAuthState();
      throw new Error('Missing PKCE verifier. Please start the login flow again.');
    }

    try {
      const response = await fetch(`${API_BASE}/v2/auth/token.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: CLIENT_ID,
          code: payload.code,
          redirect_uri: payload.redirectUri,
          code_verifier: verifier
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || errorBody?.message || 'Failed to exchange authorization code.');
      }

      const data = (await response.json()) as {
        access_token: string;
        token_type: string;
        scope: string[];
        user_id: number;
      };

      tokenStore.set(data.access_token);

      return data;
    } finally {
      clearPkceVerifier();
      clearOAuthState();
    }
  },
  logout: () =>
    request<{ success: boolean; data: { message: string } }>(
      '/v2/auth/logout.php',
      {
        method: 'POST'
      }
    ),
  startPasskeyLogin: (identifier: string) =>
    request('/v2/passkey/login/start.php', {
      method: 'POST',
      body: JSON.stringify({ identifier })
    }),
  finishPasskeyLogin: (credential: unknown) =>
    request('/v2/passkey/login/finish.php', {
      method: 'POST',
      body: JSON.stringify(credential)
    }),
  startPasskeyRegistration: () =>
    request('/v2/passkey/register/start.php', {
      method: 'POST'
    }),
  finishPasskeyRegistration: (credential: unknown) =>
    request('/v2/passkey/register/finish.php', {
      method: 'POST',
      body: JSON.stringify(credential)
    })
};
